import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
  viewChildren,
  linkedSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { GeolocationResult, GeolocationService } from '@pedix-workspace/angular-ui-map';
import { combineLatest } from 'rxjs';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import { ApiV1_Subsidiary } from '@pedix-workspace/api-v1';
import { SubsidiaryComponent } from './subsidiary/subsidiary.component';
import { IconBuildingComponent, IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';
import { StickyHeadingComponent } from '@pedix-workspace/angular-ui-sticky-heading';
import { AppRequestContextService } from '../../../app-request-context.service';
import { InputSwitchComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { FormsModule } from '@angular/forms';
import { getDistanceFromLatLonInKm } from '@pedix-workspace/utils';
import { GeolocationErrorDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';

@Component({
  selector: 'app-subsidiaries',
  templateUrl: './subsidiaries.component.html',
  styleUrls: ['./subsidiaries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NgClass,
    StickyHeadingComponent,
    IconBuildingComponent,
    IconLoadingComponent,
    SubsidiaryComponent,
    InputSwitchComponent,
    FormsModule,
  ],
})
export class SubsidiariesComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private geolocationService = inject(GeolocationService);
  private t = inject(TranslocoService);
  private analyticsService = inject(AnalyticsService);
  private appRequestContext = inject(AppRequestContextService);
  private modalDialogService = inject(ModalDialogService);

  readonly stickyHeadings = viewChildren<ElementRef>('cityHeading');

  readonly loading = signal(true);
  readonly subsidiariesByCity = signal<{ city: string; subsidiaries: ApiV1_Subsidiary[] }[]>([]);
  readonly nearestSubsidiaries = signal<ApiV1_Subsidiary[]>([]);
  readonly orderBy = signal<'city' | 'nearest'>('city');
  readonly geolocationResult = signal<GeolocationResult | null>(null);
  readonly subsidiariesDistances = signal<Record<string, number>>({});
  readonly displayCitiesHeading = signal(false);
  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly linkedStickyHeadings = linkedSignal<ElementRef[]>(() => [...this.stickyHeadings()]);

  private orderBy$ = toObservable(this.orderBy);
  private geolocationResult$ = toObservable(this.geolocationResult);

  ngOnInit() {
    const subsidiaries = this.appRequestContext.subsidiaries();

    combineLatest([this.orderBy$, this.geolocationResult$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sortBy, geolocationResult]) => {
        this.loading.set(false);

        if (sortBy === 'city') {
          this.sortSubsidiariesByCity(subsidiaries);
        } else if (sortBy === 'nearest' && geolocationResult) {
          this.sortSubsidiariesByNearest(subsidiaries);
        }
      });
  }

  sortSubsidiariesByCity(subsidiaries: ApiV1_Subsidiary[]) {
    const subsidiariesByCityMap: Record<string, ApiV1_Subsidiary[]> = {};
    const otherSubsidiaries = [];

    const result: { city: string; subsidiaries: ApiV1_Subsidiary[] }[] = [];

    subsidiaries.forEach(subsidiary => {
      if (subsidiary.addressDetails) {
        const city = `${subsidiary.addressDetails.state} - ${subsidiary.addressDetails.locality}`;

        if (!subsidiariesByCityMap[city]) {
          subsidiariesByCityMap[city] = [];
        }
        subsidiariesByCityMap[city].push(subsidiary);
      } else {
        otherSubsidiaries.push(subsidiary);
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-shadow
    Object.entries(subsidiariesByCityMap).forEach(([city, subsidiaries]) => {
      result.push({ city, subsidiaries });
    });

    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        if (
          result[j].subsidiaries[0].addressDetails?.state ===
          result[i].subsidiaries[0].addressDetails?.state
        ) {
          const subsidiaryToMove = result[j];

          result.splice(j, 1);
          result.splice(i + 1, 0, subsidiaryToMove);

          break;
        }
      }
    }

    this.displayCitiesHeading.set(result.length > 0);

    if (otherSubsidiaries.length > 0) {
      result.push({
        city: this.t.translate('subsidiaries.otherBranches'),
        subsidiaries: otherSubsidiaries,
      });
    }

    this.subsidiariesByCity.set(result);
  }

  sortSubsidiariesByNearest(subsidiaries: ApiV1_Subsidiary[]) {
    this.displayCitiesHeading.set(false);

    const distances: Record<string, number> = {};

    subsidiaries.forEach(subsidiary => {
      const subsidiaryCoordinates = subsidiary?.addressDetails?.coordinates;
      const distance = subsidiaryCoordinates
        ? getDistanceFromLatLonInKm(this.geolocationResult(), subsidiaryCoordinates)
        : Number.MAX_SAFE_INTEGER;

      distances[subsidiary.slug] = distance;
    });

    this.subsidiariesDistances.set(distances);

    this.nearestSubsidiaries.set(
      subsidiaries.slice(0).sort((subsidiarieA, subsidiarieB) => {
        return distances[subsidiarieA.slug] - distances[subsidiarieB.slug];
      }),
    );
  }

  onOrderByCity() {
    this.orderBy.set('city');
    this.analyticsService.logEvent('subsidiaries_orderby_clicked', {
      subsidiaries_order_by: 'city',
    });
  }

  async onOrderByNearest() {
    this.orderBy.set('nearest');

    this.analyticsService.logEvent('subsidiaries_orderby_clicked', {
      subsidiaries_order_by: 'nearest',
    });

    this.loading.set(true);
    const geolocationResult = await this.geolocationService.geolocate();
    this.loading.set(false);

    if (geolocationResult) {
      this.geolocationResult.set(geolocationResult);
    } else {
      this.analyticsService.logEvent('subsidiaries_orderby_error');

      await this.modalDialogService
        .attachDialog(GeolocationErrorDialogComponent)
        .open()
        .catch(() => {});

      this.orderBy.set('city');
    }
  }
}
